import { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import {
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material'
import { NavigateBefore, NavigateNext, Search } from '@mui/icons-material'
import ErrorDisplay from 'components/ErrorDisplay'
import dayjs from 'lib/dayjs'
import { useDisablePolling } from 'lib/useDisablePolling'
import { useGetSurgeriesQuery } from 'lib/apollo/hooks'
import CustomDatePicker from 'components/molecules/DatePicker/DatePicker'
import {
  GroupedProcedureListTable,
  GroupedProcedureListTableRef,
} from './GroupedProcedureListTable'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Button from 'components/molecules/Button/Button'
import { useIsMobile } from 'lib/utils/mediaQueries'
import { useFlags } from 'launchdarkly-react-client-sdk'
import TextInput from 'components/molecules/TextInput/TextInput'
import { Surgery } from 'common/types'
import { useFilters } from 'lib/hooks/useFilters'
import { filterSurgeries } from 'lib/utils/filterSurgeries'
import ProcedureFilters from 'components/ProcedureFilters/ProcedureFilters'
import { CustomModal } from 'components/molecules/Modal/Modal'
import Notifications from 'views/Notifications/Notifications'
import { SpecialRequestModal } from './SpecialRequestModal/SpecialRequestModal'
import { SpecialRequestInfoModal } from './SpecialRequestInfoModal/SpecialRequestInfoModal'
import EditProcedureModal from './EditProcedureModal/EditProcedureModal'
import { TShowSpecialRequestModal } from './ProcedureList.types'
import InventoryProvider from 'lib/apollo/inventory-config'

// Constants for hospital names (consistent with other components)
const HOSPITAL_NAMES = [
  'General',
  'Community',
  'Childrens',
  'UCLA',
  'USC',
] as const

/**
 * Generates a deterministic hospital name based on surgery ID
 * @param surgeryId - The unique identifier for the surgery
 * @returns A consistent hospital name for the given surgery
 */
const getHospitalNameForSurgery = (surgeryId: string): string => {
  let hash = 0
  for (let i = 0; i < surgeryId.length; i++) {
    const char = surgeryId.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  const index = Math.abs(hash) % HOSPITAL_NAMES.length
  return HOSPITAL_NAMES[index]
}

const initialBefore = dayjs().endOf('day').toDate()
const initialAfter = dayjs().startOf('day').toDate()

function ProceduresNurse() {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const disablePolling = useDisablePolling()
  const [beforeDate, setBeforeDate] = useState(initialBefore)
  const [afterDate, setAfterDate] = useState(initialAfter)
  const flags = useFlags()
  const [search, setSearch] = useState<string>()
  const [surgeries, setSurgeries] = useState<Surgery[]>()
  const [filteredSurgeries, setFilteredSurgeries] = useState<Surgery[]>()
  const [filters, setFilters, clearFilters, isAnyFilterActive] = useFilters()
  const [expandedHospitals, setExpandedHospitals] = useState<Set<string>>(
    new Set()
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSurgeryId = searchParams.get('selectedSurgeryId')
  const [lastViewedSurgeryId, setLastViewedSurgeryId] = useState<string | null>(
    null
  )
  const groupedProcedureListRef = useRef<GroupedProcedureListTableRef>(null)

  const [showSpecialRequestModal, setShowSpecialRequestModal] =
    useState<TShowSpecialRequestModal>({
      showModal: null,
      surgeryId: null,
      surgeryStatus: null,
    })
  const [loadingSpecialRequests, setLoadingSpecialRequests] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState<{
    isOpen: boolean
    surgery?: Surgery
  }>({
    isOpen: false,
  })

  // Edit procedure modal state
  const [editModalSurgery, setEditModalSurgery] = useState<Surgery | null>(null)

  // Memoize the check for when the surgery is present
  const selectedSurgeryExists = useMemo(
    () => !!filteredSurgeries?.find((s) => s._id === selectedSurgeryId),
    [filteredSurgeries, selectedSurgeryId]
  )

  const { loading, error, data, refetch } = useGetSurgeriesQuery(
    {
      before: beforeDate,
      after: afterDate,
      limit: 0,
    },
    {
      disablePolling,
    }
  )

  const isErrored = !loading && error

  // Group surgeries by hospital
  const groupedSurgeries = useMemo(() => {
    if (!filteredSurgeries) return {}

    const groups: Record<string, Surgery[]> = {}
    filteredSurgeries.forEach((surgery) => {
      const hospitalName = getHospitalNameForSurgery(surgery._id)
      const hospitalKey = `${hospitalName} Hospital, Los Angeles, CA` // Mock location format

      if (!groups[hospitalKey]) {
        groups[hospitalKey] = []
      }
      groups[hospitalKey].push(surgery)
    })

    return groups
  }, [filteredSurgeries])

  /**
   * Scrolls to a specific surgery case and highlights it
   */
  const scrollToSurgery = useCallback(
    (surgeryId: string) => {
      if (!surgeryId) return

      // Find the surgery to determine which hospital it belongs to
      const surgery = filteredSurgeries?.find((s) => s._id === surgeryId)
      if (!surgery) return

      const hospitalName = getHospitalNameForSurgery(surgery._id)

      // Expand the hospital section first
      if (groupedProcedureListRef.current) {
        groupedProcedureListRef.current.toggleHospital(hospitalName)
      }

      // Wait for the hospital section to expand and then scroll to the surgery
      setTimeout(() => {
        // Look for the surgery card container (which includes the trays section)
        const surgeryElement =
          document.getElementById(surgeryId) ||
          document.querySelector(`[data-surgery-id="${surgeryId}"]`) ||
          document.querySelector(`[data-testid="test-procedure-${surgeryId}"]`)

        if (surgeryElement) {
          // Scroll to the surgery with offset to show the trays section
          surgeryElement.scrollIntoView({ behavior: 'smooth', block: 'end' })

          // Apply highlight to the surgery card container
          surgeryElement.classList.add('highlight-surgery')

          // Remove highlight after animation
          setTimeout(() => {
            surgeryElement.classList.remove('highlight-surgery')
          }, 2000)
        }
      }, 500) // Increased delay to allow hospital expansion
    },
    [filteredSurgeries]
  )

  /**
   * Updates date filters from URL parameters and cleans up the URL
   */
  const updateDateFiltersFromUrl = useCallback(() => {
    const urlBeforeDate = searchParams.get('beforeDate')
    const urlAfterDate = searchParams.get('afterDate')
    const urlSelectedSurgeryId = searchParams.get('selectedSurgeryId')

    // Only update if all required parameters are present
    if (!urlBeforeDate || !urlAfterDate || !urlSelectedSurgeryId) {
      return
    }

    try {
      const beforeDateObj = new Date(urlBeforeDate)
      const afterDateObj = new Date(urlAfterDate)

      // Validate that dates are valid
      if (isNaN(beforeDateObj.getTime()) || isNaN(afterDateObj.getTime())) {
        throw new Error('Invalid date format in URL parameters')
      }

      // Store the surgery ID before resetting params
      setLastViewedSurgeryId(urlSelectedSurgeryId)

      setBeforeDate(beforeDateObj)
      setAfterDate(afterDateObj)

      // Clean up URL parameters
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('beforeDate')
      newSearchParams.delete('afterDate')
      newSearchParams.delete('selectedSurgeryId')
      setSearchParams(newSearchParams, { replace: true })
    } catch (error) {
      console.error('Error parsing date parameters:', error)
    }
  }, [searchParams, setSearchParams])

  // Update date filters when URL parameters change
  useEffect(() => {
    updateDateFiltersFromUrl()
  }, [updateDateFiltersFromUrl])

  // Scroll to surgery after data is loaded and surgery exists
  useEffect(() => {
    if (lastViewedSurgeryId && !loading && filteredSurgeries) {
      const surgeryExists = filteredSurgeries.some(
        (s) => s._id === lastViewedSurgeryId
      )
      if (surgeryExists) {
        scrollToSurgery(lastViewedSurgeryId)
      }
    }
  }, [lastViewedSurgeryId, loading, filteredSurgeries, scrollToSurgery])

  const handleBackDay = () => {
    setBeforeDate(dayjs(beforeDate).subtract(1, 'day').toDate())
    setAfterDate(dayjs(afterDate).subtract(1, 'day').toDate())
  }

  const handleForwardDay = () => {
    setBeforeDate(dayjs(beforeDate).add(1, 'day').toDate())
    setAfterDate(dayjs(afterDate).add(1, 'day').toDate())
  }

  const handleDateChange = (date: Date) => {
    setBeforeDate(dayjs(date).endOf('day').toDate())
    setAfterDate(dayjs(date).startOf('day').toDate())
  }

  const handleNavigate = () => navigate('/new-procedure')

  const handleCloseSpecialRequest = () => {
    setShowSpecialRequestModal({
      showModal: null,
      surgeryId: null,
      surgeryStatus: null,
    })
  }

  const handleCloseEditModal = () => {
    setEditModalSurgery(null)
  }

  const applySearchAndFilters = useCallback(() => {
    if (!surgeries) {
      setFilteredSurgeries(undefined)
      return
    }

    let results = surgeries

    if (search) {
      const searchTerm = search.toLowerCase()

      results = surgeries.filter((surgery: Surgery) => {
        const surgeonName = `${
          surgery.visit?.attendingProvider?.firstName || ''
        } ${surgery.visit?.attendingProvider?.lastName || ''}`.toLowerCase()

        const firstInitial =
          surgery.patient.firstName?.charAt(0)?.toLowerCase() || ''
        const lastInitial =
          surgery.patient.lastName?.charAt(0)?.toLowerCase() || ''

        const initialPatterns = [
          `${firstInitial}${lastInitial}`,
          `${lastInitial}${firstInitial}`,
          `${firstInitial} ${lastInitial}`,
          `${lastInitial} ${firstInitial}`,
          `${firstInitial}, ${lastInitial}`,
          `${lastInitial}, ${firstInitial}`,
          firstInitial,
          lastInitial,
        ].filter((pattern) => pattern.trim().length > 0)

        const matchesInitials = initialPatterns.some(
          (pattern) =>
            pattern.includes(searchTerm) || searchTerm.includes(pattern)
        )

        const hospitalName = getHospitalNameForSurgery(
          surgery._id
        ).toLowerCase()
        const facilityName = (
          surgery.visit?.location?.department ||
          surgery.visit?.location?.type ||
          surgery.location?.department ||
          surgery.location?.type ||
          ''
        ).toLowerCase()

        return (
          surgeonName.includes(searchTerm) ||
          matchesInitials ||
          facilityName.includes(searchTerm) ||
          hospitalName.includes(searchTerm)
        )
      })
    }

    results = filterSurgeries(results, filters)
    setFilteredSurgeries(results)
  }, [surgeries, search, filters])

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm)
  }, [])

  const handleApplyFilters = useCallback(
    (newFilters) => {
      setFilters(newFilters)
    },
    [setFilters]
  )

  // Filter removal handlers
  const handleRemoveLocationFilter = useCallback(
    (location) => {
      setFilters({
        ...filters,
        locations: filters.locations.filter((loc) => loc !== location),
      })
    },
    [filters, setFilters]
  )

  const handleRemoveRoomFilter = useCallback(
    (room) => {
      setFilters({
        ...filters,
        rooms: filters.rooms.filter((r) => r !== room),
      })
    },
    [filters, setFilters]
  )

  const handleRemoveSurgeonFilter = useCallback(() => {
    setFilters({
      ...filters,
      surgeonName: null,
    })
  }, [filters, setFilters])

  const handleRemoveSurgeryFilter = useCallback(() => {
    setFilters({
      ...filters,
      surgeryName: null,
    })
  }, [filters, setFilters])

  useEffect(() => {
    if (data?.getSurgeries?.results) {
      setSurgeries(data?.getSurgeries?.results)
    }
  }, [data?.getSurgeries?.results])

  useEffect(() => {
    applySearchAndFilters()
  }, [surgeries, search, filters, applySearchAndFilters])

  // Auto-expand all hospitals on initial load
  useEffect(() => {
    if (groupedSurgeries && Object.keys(groupedSurgeries).length > 0) {
      setExpandedHospitals(new Set(Object.keys(groupedSurgeries)))
    }
  }, [groupedSurgeries])

  const getActiveFilterCounts = () => {
    return {
      sites: filters.locations.length,
      locations: filters.rooms.length,
      surgeon: filters.surgeonName ? 1 : 0,
      procedure: filters.surgeryName ? 1 : 0,
    }
  }

  const activeFilterCounts = getActiveFilterCounts()
  const totalActiveFilters = Object.values(activeFilterCounts).reduce(
    (a, b) => a + b,
    0
  )

  return (
    <Box
      aria-label="procedure-list-container"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      gap={{ xs: 1.5, sm: 2 }}
      width="100%"
      sx={{
        padding: { xs: 1, sm: 1.5, md: 0 },
      }}
    >
      {/* Header */}
      <Box
        aria-label="procedures-nurse-header"
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent={{ xs: 'center', sm: 'space-between' }}
        gap={{ xs: 1, sm: 0 }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 18, sm: 20, md: 24 },
            textAlign: { xs: 'center', sm: 'left' },
          }}
          className="procedure-list-header"
        >
          Vendor Case Log
        </Typography>

        {!isMobile ? (
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: { sm: 14, md: 16, lg: 20 },
              fontWeight: 500,
            }}
          >
            {dayjs().format('ddd MMM D, YYYY')}
          </Typography>
        ) : null}

        {flags?.allowAddingProceduresManually ? (
          <Button
            onClick={handleNavigate}
            variant="contained"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
            }}
          >
            Add Case/ Procedure
          </Button>
        ) : null}
      </Box>

      {/* Date Picker */}
      <Box
        aria-label="date-picker-box"
        justifyContent="center"
        alignItems="center"
        flexDirection="row"
        display="flex"
        sx={{
          gap: { xs: 1, sm: 2 },
          padding: { xs: 1, sm: 0 },
        }}
      >
        <IconButton
          onClick={handleBackDay}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            padding: { xs: 0.5, sm: 1 },
          }}
        >
          <NavigateBefore fontSize={isMobile ? 'medium' : 'large'} />
        </IconButton>

        <CustomDatePicker
          label="Procedure List Date"
          date={afterDate}
          renderDatePicker={true}
          onDateSelection={handleDateChange}
        />

        <IconButton
          onClick={handleForwardDay}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            padding: { xs: 0.5, sm: 1 },
          }}
        >
          <NavigateNext fontSize={isMobile ? 'medium' : 'large'} />
        </IconButton>
      </Box>

      {/* Error Handling */}
      {isErrored && !surgeries && <ErrorDisplay pathForward={refetch} />}

      {isErrored && surgeries && (
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            padding: { xs: 1, sm: 0 },
          }}
        >
          Our server encountered an issue with 1 or more surgeries that could
          not be displayed. Please contact the administrator for further
          assistance.
        </Typography>
      )}

      {/* Search and Filters */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={{ xs: 1, sm: 2 }}
        sx={{
          backgroundColor: '#ffffff',
          padding: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box flexGrow={1}>
          <TextInput
            variant="outlined"
            size="small"
            startAdornment={<Search style={{ color: '#999' }} />}
            value={search}
            placeholder={`Search ${
              isMobile
                ? '(Patient, Surgeon, Hospital)'
                : '(by Patient Initials, Surgeon, or Hospital Name)'
            }`}
            handleChangeFormData={(e: any) => handleSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f9f9f9',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                },
              },
            }}
          />
        </Box>

        <ProcedureFilters
          filters={filters}
          surgeries={surgeries}
          onClearFilters={clearFilters}
          onApplyFilters={handleApplyFilters}
          isAnyFilterActive={isAnyFilterActive}
        />
      </Box>

      {/* Active Filters */}
      {totalActiveFilters > 0 && (
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            padding: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            border: '1px solid #e9ecef',
          }}
        >
          <Box
            display="flex"
            flexDirection="row"
            gap={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <Typography
              variant="body2"
              sx={{
                color: '#6c757d',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              }}
            >
              Active filters:
            </Typography>

            {filters.locations.map((location) => (
              <Chip
                key={`location-${location}`}
                label={location}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => handleRemoveLocationFilter(location)}
                sx={{
                  backgroundColor: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 24, sm: 28 },
                  '& .MuiChip-deleteIcon': {
                    color: '#5035ed',
                  },
                }}
              />
            ))}

            {filters.rooms.map((room) => (
              <Chip
                key={`room-${room}`}
                label={`Hospital ${room}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => handleRemoveRoomFilter(room)}
                sx={{
                  backgroundColor: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 24, sm: 28 },
                  '& .MuiChip-deleteIcon': {
                    color: '#5035ed',
                  },
                }}
              />
            ))}

            {activeFilterCounts.surgeon > 0 && (
              <Chip
                label={`Surgeon: ${filters.surgeonName}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={handleRemoveSurgeonFilter}
                sx={{
                  backgroundColor: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 24, sm: 28 },
                  '& .MuiChip-deleteIcon': {
                    color: '#5035ed',
                  },
                }}
              />
            )}

            {activeFilterCounts.procedure > 0 && (
              <Chip
                label={`Surgery: ${filters.surgeryName}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={handleRemoveSurgeryFilter}
                sx={{
                  backgroundColor: 'white',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 24, sm: 28 },
                  '& .MuiChip-deleteIcon': {
                    color: '#5035ed',
                  },
                }}
              />
            )}

            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={clearFilters}
              sx={{
                ml: 1,
                minWidth: 'auto',
                p: '4px 12px',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 500,
              }}
            >
              Clear All
            </Button>
          </Box>
        </Box>
      )}

      <InventoryProvider>
        <GroupedProcedureListTable
          events={filteredSurgeries}
          loading={loading}
          beforeDate={beforeDate}
          afterDate={afterDate}
          onTrayAssignmentChange={refetch}
          ref={groupedProcedureListRef}
          expandTraysForSurgeryId={lastViewedSurgeryId}
        />
      </InventoryProvider>

      {/* Notifications Modal */}
      <CustomModal
        fitContentWidth={isMobile}
        open={isNotificationsOpen.isOpen}
        header="Notifications"
        isLoadingState={false}
        secondaryButtonAction={() => setIsNotificationsOpen({ isOpen: false })}
        primaryButtonDisabled
        closeButtonOnly
      >
        <Notifications
          surgery={isNotificationsOpen?.surgery as Surgery}
          isMobile={isMobile}
        />
      </CustomModal>

      {/* Special Request Modals */}
      {showSpecialRequestModal.showModal === 'add' && (
        <SpecialRequestModal
          surgeryId={showSpecialRequestModal.surgeryId as string}
          onClose={handleCloseSpecialRequest}
          beforeDate={beforeDate}
          afterDate={afterDate}
        />
      )}

      {loadingSpecialRequests && (
        <div className="loader">
          <CircularProgress />
        </div>
      )}

      {showSpecialRequestModal.showModal === 'show' && (
        <SpecialRequestInfoModal
          canAdd={showSpecialRequestModal.surgeryStatus === 'SCANNING'}
          showModal={!loadingSpecialRequests}
          surgeryId={showSpecialRequestModal.surgeryId as string}
          onClose={handleCloseSpecialRequest}
          onHeaderButtonAction={(surgeryId) => {
            handleCloseSpecialRequest()
            setShowSpecialRequestModal((prev) => ({
              ...prev,
              showModal: 'add',
              surgeryId,
            }))
          }}
          onFinishLoading={setLoadingSpecialRequests}
        />
      )}

      {/* Edit Procedure Modal */}
      {editModalSurgery && (
        <EditProcedureModal
          open={!!editModalSurgery}
          onClose={handleCloseEditModal}
          surgery={editModalSurgery}
          beforeDate={beforeDate}
          afterDate={afterDate}
        />
      )}
    </Box>
  )
}

export default ProceduresNurse
